import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Truck, Package, FileText, Calculator, GraduationCap, MapPin, ArrowRight,
  Lightning, ShieldCheck, Clock, Star, CheckCircle, Path, Storefront,
  ChartLineUp, MapTrifold, Bank, Handshake, Newspaper, Microphone, Rocket, CalendarBlank,
} from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LeadDialog } from "@/components/LeadDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LoadCard } from "@/components/LoadCard";
import { RequestLoadDialog } from "@/components/RequestLoadDialog";
import { getLoads, OWNER } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1771931438145-434154f5f7e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwY29hc3RhbHxlbnwwfHx8fDE3ODYwNDcwNzB8MA&ixlib=rb-4.1.0&q=85";
const HERO_VIDEO = "/hero.mp4";
const HOUSTON_IMG = "https://images.unsplash.com/photo-1622007151631-25aa98ab394b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxob3VzdG9uJTIwc2t5bGluZSUyMG1vZGVybnxlbnwwfHx8fDE3ODYwNDcwNzB8MA&ixlib=rb-4.1.0&q=85";
const OWNER_IMG = "https://images.unsplash.com/photo-1505165697550-710e168b709d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHx0cnVja2luZyUyMGJ1c2luZXNzJTIwb3duZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODYwNDcwNjl8MA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { icon: Truck, title: "Freight & Expedited", tags: ["Expedited / Hotshot", "Dry Van", "Flatbed", "Reefer", "OTR"], accent: true },
  { icon: Storefront, title: "Dispatch, Load Board & Marketplace", tags: ["24/7 dispatch", "Live load matching", "Carrier marketplace"] },
  { icon: FileText, title: "Filings & Compliance", tags: ["MC/DOT", "BOC-3", "UCR", "IRP/IFTA", "Form 2290", "Drug & Alcohol Consortium"] },
  { icon: Calculator, title: "Bookkeeping & Back-Office", tags: ["Monthly bookkeeping", "Settlement reconciliation", "IFTA reporting"] },
  { icon: GraduationCap, title: "Education & Startup", tags: ["LLC / EIN setup", "Authority filing", "1-on-1 mentorship"] },
];

const STEPS = [
  { n: "01", title: "Form Your Business", desc: "LLC + EIN filed. We set the legal foundation." },
  { n: "02", title: "Get Authority", desc: "MC/DOT numbers, BOC-3, UCR, insurance guidance." },
  { n: "03", title: "Get Compliant", desc: "IRP/IFTA, 2290, drug & alcohol consortium enrollment." },
  { n: "04", title: "Start Hauling", desc: "Loads, dispatch, factoring, and mentorship on day one." },
];

const STATS = [
  { v: "24/7", l: "Dispatch Coverage" },
  { v: "$2.3M+", l: "Loads Moved Monthly" },
  { v: "500+", l: "Carriers Onboarded" },
  { v: "48hr", l: "Avg. Authority Setup" },
];

const TESTIMONIALS = [
  { name: "Marcus D.", role: "Owner-Operator, Houston", text: "Coastal set up my authority in two days and had me hauling by the weekend. The dispatch team actually answers the phone." },
  { name: "Alicia R.", role: "Fleet Owner, Dallas", text: "Their factoring and bookkeeping freed up my cash flow. I finally understand my numbers." },
  { name: "Jerome T.", role: "New Carrier, San Antonio", text: "I knew nothing about starting a trucking company. The mentorship program walked me through every filing." },
];

const PRODUCTS = [
  { icon: Storefront, name: "One Marketplace", desc: "A single truckload freight marketplace connecting shippers, brokers, and carriers on every lane." },
  { icon: ChartLineUp, name: "iQ Analytics", desc: "Real-time rate intelligence and market analytics to price freight and win more loads." },
  { icon: Lightning, name: "Convoy Matching", desc: "Automated freight-matching technology that pairs the right truck with the right load instantly." },
  { icon: MapTrifold, name: "Trucker Tools", desc: "Load-visibility platform with live GPS tracking and digital check-calls from pickup to delivery." },
  { icon: Bank, name: "Financial Services", desc: "Fuel cards, banking, and back-office tools built for owner-operators and small fleets." },
];

const AUDIENCE = [
  { icon: Truck, name: "Carriers", desc: "Find loads, get dispatched, and get paid faster.", to: "carriers" },
  { icon: Handshake, name: "Brokers", desc: "Source capacity and move freight with confidence.", to: "brokers" },
  { icon: Package, name: "Shippers", desc: "Post loads and reach thousands of vetted carriers.", to: "shippers" },
];

const RESOURCES = [
  { icon: ChartLineUp, name: "Trendlines", desc: "A weekly snapshot of industry trends.", tag: "Weekly",
    intro: "This week in freight — Gulf Coast & national spot market snapshot.",
    items: [
      { title: "Van spot rate: $2.41/mi", sub: "▲ 3% week-over-week" },
      { title: "Reefer spot rate: $2.78/mi", sub: "▲ 1.5% — produce season lift" },
      { title: "Flatbed spot rate: $2.66/mi", sub: "▼ 0.8% — steady demand" },
      { title: "Van load-to-truck ratio: 4.2", sub: "Texas running hot vs. 3.1 national" },
      { title: "Diesel average: $3.84/gal", sub: "▼ $0.05 — eases margins" },
      { title: "Hottest lane: Houston → Dallas", sub: "Highest posted volume this week" },
    ] },
  { icon: Newspaper, name: "Blog", desc: "Stay up to date on the latest in trucking.", tag: "",
    intro: "Practical playbooks and how-tos from the Coastal dispatch desk.",
    items: [
      { title: "5 Ways Owner-Operators Boost Rate Per Mile", sub: "Cut deadhead, target backhauls, negotiate detention." },
      { title: "Hotshot vs. Expedited: Which Pays More in Texas?", sub: "Break-even math on the Permian oilfield lanes." },
      { title: "Detention 101 — How to Actually Get Paid", sub: "Document, notify, and invoice the right way." },
      { title: "MC/DOT Authority: A Step-by-Step Setup", sub: "From EIN to active authority in ~48 hours." },
    ] },
  { icon: Microphone, name: "Podcast", desc: "Listen to industry experts.", tag: "",
    intro: "Coastal Freight Radio — real talk with carriers, brokers, and dispatchers.",
    items: [
      { title: "Ep. 12 — Surviving a Soft Freight Market", sub: "38 min · lane selection & cash flow" },
      { title: "Ep. 11 — From One Truck to a Fleet of Ten", sub: "45 min · scaling without breaking" },
      { title: "Ep. 10 — Fuel Cards & Cash Flow 101", sub: "29 min · keeping more per mile" },
      { title: "Ep. 09 — Winning Your First Shipper Contract", sub: "33 min · direct freight tips" },
    ] },
  { icon: Rocket, name: "Start-Up Guides", desc: "Discover how to grow your business.", tag: "",
    intro: "Your roadmap from idea to your first load — the Coastal way.",
    items: [
      { title: "1. Form Your LLC & Get Your EIN", sub: "Set the legal foundation the right way." },
      { title: "2. File for MC/DOT Authority", sub: "BOC-3, UCR, and insurance guidance." },
      { title: "3. Get Compliant", sub: "IRP/IFTA, Form 2290, drug & alcohol consortium." },
      { title: "4. Land Your First Loads", sub: "Dispatch, load board access, and mentorship." },
    ] },
  { icon: CalendarBlank, name: "News & Events", desc: "Coastal happenings and industry events.", tag: "",
    intro: "What's happening at Coastal and across the Texas freight scene.",
    items: [
      { title: "Coastal at the Great American Trucking Show", sub: "Aug 2026 · Houston, TX — booth #412" },
      { title: "New: Hotshot lanes added to the board", sub: "Permian oilfield freight now live" },
      { title: "Houston Owner-Operator Meetup", sub: "First Friday monthly · 801 Travis St" },
      { title: "Carrier onboarding webinar", sub: "Every Tuesday · free registration" },
    ] },
  { icon: GraduationCap, name: "University", desc: "Free online courses from industry experts.", tag: "Free",
    intro: "Free, self-paced courses to sharpen your business — no cost, ever.",
    items: [
      { title: "Load Board Mastery", sub: "45 min · find and book better freight" },
      { title: "Negotiating Rates Like a Pro", sub: "30 min · get more per mile" },
      { title: "Bookkeeping Basics for Truckers", sub: "60 min · settlements, IFTA, taxes" },
      { title: "Compliance & DOT Audits", sub: "40 min · pass every inspection" },
    ] },
];

export default function Landing() {
  const navigate = useNavigate();
  const [leadType, setLeadType] = useState(null);
  const [previewLoads, setPreviewLoads] = useState([]);
  const [reqLoad, setReqLoad] = useState(null);
  const [activeResource, setActiveResource] = useState(null);

  useEffect(() => {
    getLoads({ sort: "highest_rate" }).then((d) => setPreviewLoads(d.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      <Navbar onQuote={() => setLeadType("quote")} />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0">
          <video
            autoPlay muted loop playsInline
            poster={HOUSTON_IMG}
            className="w-full h-full object-cover"
            data-testid="hero-video"
            preload="auto"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1220] via-[#0B1220]/85 to-[#0B1220]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-transparent to-[#0B1220]/30" />
          <div className="noise-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-center py-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#2563EB]/40 bg-[#2563EB]/10 text-[#60A5FA] text-xs uppercase tracking-[0.2em] mb-6">
              <Star size={14} weight="fill" className="text-[#2563EB]" /> Houston, Texas · Serving All 48 States
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
              Houston's operating<br />system for <span className="text-[#2563EB]">carriers.</span>
            </h1>
            <p className="text-lg text-[#94A3B8] max-w-xl mb-8 leading-relaxed">
              Texas-built freight, dispatch, compliance, bookkeeping, and company startup — one Houston team that keeps American truckers rolling and their businesses growing.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/portal" data-testid="hero-post-load-btn" className="px-7 py-3.5 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors flex items-center gap-2 aqua-glow">
                <Package size={20} /> Post a Load
              </Link>
              <Link to="/loads" data-testid="hero-find-loads-btn" className="px-7 py-3.5 rounded-full border border-white/20 text-white font-semibold hover:border-[#2563EB] hover:text-[#2563EB] transition-colors flex items-center gap-2">
                <Path size={20} /> Find Loads
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-white/10 bg-[#111A2E]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-wrap items-center justify-center lg:justify-between gap-6 text-sm">
          <div className="flex items-center gap-2 text-[#94A3B8]"><MapPin size={18} className="text-[#2563EB]" /> Houston, Texas HQ</div>
          <div className="hidden lg:block w-px h-5 bg-white/10" />
          <div className="flex items-center gap-2 text-[#94A3B8]"><ShieldCheck size={18} className="text-[#2563EB]" /> US DOT / MC Authority</div>
          <div className="hidden lg:block w-px h-5 bg-white/10" />
          <div className="flex items-center gap-2 text-[#94A3B8]"><Clock size={18} className="text-[#2563EB]" /> 24/7 Dispatch</div>
          <div className="hidden lg:block w-px h-5 bg-white/10" />
          <div className="flex items-center gap-2 text-[#94A3B8]"><Star size={18} weight="fill" className="text-[#60A5FA]" /> Serving all 48 states</div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-3">What we do</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-4">Everything a carrier needs, in one place</h2>
            <p className="text-[#94A3B8] text-lg">From your first filing to your thousandth load — Coastal runs the back office so you can run the road.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`service-card-${i}`}
                className={`group p-8 rounded-2xl border transition-transform duration-300 hover:-translate-y-1 ${s.accent ? "bg-[#2563EB]/5 border-[#2563EB]/30" : "bg-[#111A2E] border-white/10 hover:border-[#2563EB]/40"}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${s.accent ? "bg-[#2563EB] text-white" : "bg-[#2563EB]/10 text-[#2563EB]"}`}>
                  <s.icon size={26} weight="bold" />
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight mb-4">{s.title}</h3>
                <ul className="space-y-2">
                  {s.tags.map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-[#94A3B8]"><CheckCircle size={15} weight="fill" className="text-[#60A5FA] shrink-0" /> {t}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM / PRODUCTS */}
      <section id="platform" className="py-24 lg:py-32 bg-[#111A2E] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-3 flex items-center gap-2"><Star size={13} weight="fill" className="text-[#60A5FA]" /> The platform</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-4">One platform, the whole supply chain</h2>
            <p className="text-[#94A3B8] text-lg">Trusted by shippers, transportation brokers, carriers, news organizations, and industry analysts — Coastal sets the standard for innovation in trucking and logistics.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRODUCTS.map((p, i) => (
              <div key={p.name} data-testid={`product-card-${i}`} className="group p-8 rounded-2xl bg-[#0B1220] border border-white/10 hover:border-[#2563EB]/40 transition-transform duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center mb-5"><p.icon size={26} weight="bold" /></div>
                <h3 className="font-display text-xl font-bold tracking-tight mb-2">{p.name}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section id="solutions" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-3">Who we serve</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">Find the solution right for you</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {AUDIENCE.map((a) => (
              <button key={a.name} onClick={() => navigate(`/pricing?for=${a.to}`)} data-testid={`audience-card-${a.name.toLowerCase()}`} className="text-left group p-8 rounded-2xl bg-[#111A2E] border border-white/10 hover:border-[#2563EB]/40 transition-transform duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 rounded-xl bg-[#60A5FA]/10 text-[#60A5FA] flex items-center justify-center mb-5"><a.icon size={26} weight="bold" /></div>
                <h3 className="font-display text-2xl font-bold tracking-tight mb-2">{a.name}</h3>
                <p className="text-sm text-[#94A3B8] mb-5">{a.desc}</p>
                <span className="text-[#2563EB] font-semibold text-sm flex items-center gap-1.5">View plans <ArrowRight size={16} /></span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* LOAD BOARD PREVIEW */}
      <section className="py-24 lg:py-32 bg-[#111A2E] border-y border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-3">Live loads</div>
              <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">Top-paying lanes right now</h2>
            </div>
            <Link to="/loads" data-testid="see-all-loads-btn" className="px-6 py-3 rounded-full border border-[#2563EB]/40 text-[#2563EB] font-semibold hover:bg-[#2563EB] hover:text-white transition-colors flex items-center gap-2 self-start">
              See all loads <ArrowRight size={18} />
            </Link>
          </div>
          <div className="space-y-4">
            {previewLoads.map((l, i) => <LoadCard key={l.id} load={l} index={i} onRequest={setReqLoad} />)}
          </div>
        </div>
      </section>

      {/* STARTUP */}
      <section id="startup" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-3">Start a company</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-6">Start your trucking company the right way</h2>
            <p className="text-[#94A3B8] text-lg mb-10">We've launched hundreds of Texas carriers. Follow the Coastal roadmap and skip the guesswork.</p>
            <div className="space-y-6">
              {STEPS.map((s) => (
                <div key={s.n} className="flex gap-5" data-testid={`startup-step-${s.n}`}>
                  <div className="font-display text-2xl font-extrabold text-[#2563EB]/40 w-12 shrink-0">{s.n}</div>
                  <div className="border-l border-white/10 pl-5 pb-2">
                    <h4 className="font-display text-lg font-bold mb-1">{s.title}</h4>
                    <p className="text-sm text-[#94A3B8]">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setLeadType("startup_consult")} data-testid="startup-consult-btn" className="mt-10 px-7 py-3.5 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors flex items-center gap-2">
              <GraduationCap size={20} /> Book a Free Consult
            </button>
          </div>
          <div className="relative">
            <img src={OWNER_IMG} alt="Trucking mentor" className="rounded-2xl w-full object-cover aspect-[4/5] border border-white/10" />
            <div className="absolute -bottom-6 -left-6 bg-[#111A2E] border border-[#2563EB]/30 rounded-xl p-5 max-w-[220px] aqua-glow hidden sm:block">
              <div className="font-display text-3xl font-extrabold text-[#2563EB]">48hr</div>
              <div className="text-xs text-[#94A3B8] mt-1">Average time to get your authority filed & active</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY / STATS */}
      <section id="about" className="py-24 bg-[#111A2E] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-3 flex items-center gap-2"><Star size={13} weight="fill" className="text-[#60A5FA]" /> Why Coastal</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">Houston's freight backbone</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((s) => (
              <div key={s.l} className="text-center lg:text-left" data-testid={`stat-${s.l}`}>
                <div className="font-display text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#2563EB]">{s.v}</div>
                <div className="text-sm text-[#94A3B8] mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-3">Carriers on Coastal</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">Trusted across Texas</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={t.name} className="p-8 rounded-2xl bg-[#111A2E] border border-white/10" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={`${t.name}-star-${j}`} size={16} weight="fill" className="text-[#60A5FA]" />)}</div>
                <p className="text-[#cbd5e1] mb-6 leading-relaxed">"{t.text}"</p>
                <div className="font-display font-bold">{t.name}</div>
                <div className="text-sm text-[#94A3B8]">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESOURCES */}
      <section id="resources" className="py-24 lg:py-32 bg-[#111A2E] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-3">Resources & Learning</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">Stay ahead of the freight market</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {RESOURCES.map((r, i) => (
              <button key={r.name} onClick={() => setActiveResource(r)} data-testid={`resource-card-${i}`} className="text-left group p-7 rounded-2xl bg-[#0B1220] border border-white/10 hover:border-[#2563EB]/40 transition-transform duration-300 hover:-translate-y-1 flex items-start gap-4">
                <div className="w-11 h-11 shrink-0 rounded-xl bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center"><r.icon size={22} weight="bold" /></div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-display text-lg font-bold tracking-tight">{r.name}</h3>
                    {r.tag && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#60A5FA]/15 text-[#60A5FA]">{r.tag}</span>}
                  </div>
                  <p className="text-sm text-[#94A3B8]">{r.desc}</p>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-[#2563EB] font-semibold text-xs">Explore <ArrowRight size={14} /></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* HOUSTON MAP / CONTACT */}
      <section id="contact" className="py-24 lg:py-32 bg-[#111A2E] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative rounded-2xl overflow-hidden border border-white/10">
            <img src={HOUSTON_IMG} alt="Houston skyline" className="w-full object-cover aspect-video" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220] to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2 text-white font-display font-bold">
              <MapPin size={20} weight="fill" className="text-[#2563EB] shrink-0" /> 801 Travis St, Houston, TX 77002
            </div>
          </div>
          <div>
            <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-3">Get started</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-6">Let's move your freight</h2>
            <p className="text-[#94A3B8] text-lg mb-8">Shippers, carriers, and future owners — reach out and Coastal dispatch will get right back to you.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <button onClick={() => setLeadType("quote")} data-testid="contact-quote-btn" className="p-5 rounded-xl bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors text-left">
                <Package size={24} className="mb-2" /> Get a Quote <div className="text-xs font-normal opacity-80 mt-1">For shippers</div>
              </button>
              <button onClick={() => setLeadType("carrier")} data-testid="contact-carrier-btn" className="p-5 rounded-xl border border-white/15 hover:border-[#2563EB] transition-colors text-left">
                <Truck size={24} className="mb-2 text-[#2563EB]" /> Carrier Signup <div className="text-xs opacity-70 mt-1">Drivers & fleets</div>
              </button>
              <button onClick={() => setLeadType("startup_consult")} data-testid="contact-startup-btn" className="p-5 rounded-xl border border-white/15 hover:border-[#2563EB] transition-colors text-left">
                <GraduationCap size={24} className="mb-2 text-[#2563EB]" /> Startup Consult <div className="text-xs opacity-70 mt-1">New companies</div>
              </button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-6 text-sm text-[#94A3B8]">
              <span className="flex items-center gap-2"><MapPin size={16} className="text-[#2563EB]" /> 801 Travis St, Houston, TX 77002</span>
            </div>
            <p className="text-sm text-[#64748b] mt-3">All inquiries go straight to {OWNER.email} · {OWNER.phone}</p>
          </div>
        </div>
      </section>

      <Footer />

      <LeadDialog open={!!leadType} onOpenChange={(v) => !v && setLeadType(null)} type={leadType || "quote"} />
      <RequestLoadDialog open={!!reqLoad} onOpenChange={(v) => !v && setReqLoad(null)} load={reqLoad} />

      <Dialog open={!!activeResource} onOpenChange={(v) => !v && setActiveResource(null)}>
        <DialogContent className="bg-[#111A2E] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="resource-dialog">
          {activeResource && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-lg bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center"><activeResource.icon size={20} weight="bold" /></span>
                  {activeResource.name}
                  {activeResource.tag && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#60A5FA]/15 text-[#60A5FA]">{activeResource.tag}</span>}
                </DialogTitle>
                <DialogDescription className="text-[#94A3B8]">{activeResource.intro}</DialogDescription>
              </DialogHeader>
              <div className="mt-2 space-y-2">
                {activeResource.items.map((it, idx) => (
                  <div key={it.title} data-testid={`resource-item-${idx}`} className="flex items-start gap-3 p-3.5 rounded-xl bg-[#0B1220] border border-white/10 hover:border-[#2563EB]/40 transition-colors">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#2563EB] shrink-0" />
                    <div>
                      <div className="font-semibold text-[15px]">{it.title}</div>
                      <div className="text-sm text-[#94A3B8]">{it.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => { setActiveResource(null); setLeadType("quote"); }} data-testid="resource-cta-btn" className="mt-4 w-full py-3 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors">
                Get the full {activeResource.name} — talk to Coastal
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
